import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWebSocket } from '@/lib/websocket'
import { getWebSocketUrl } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import { useToastNotificationStore } from '@/lib/toast-notifications'

/**
 * Centralized ticket WebSocket hook - handles connection and real-time updates
 *
 * This hook:
 * 1. Establishes WebSocket connection to the server with siteId
 * 2. Listens for ticket events (TICKET_REPLY_CREATED, TICKET_STATUS_UPDATED, etc.)
 * 3. Automatically invalidates TanStack Query caches for real-time UI updates
 *
 * Usage: Call this hook once at the app level (e.g., in TicketWebSocketProvider)
 *
 * Note: The WebSocket URL includes siteId as a query param, which is captured
 * by the server during upgrade and associated with the connection automatically.
 * This allows broadcastToSite() to work without modifying core WebSocket registration.
 *
 * Real-time updates work for:
 * - Admin ticket detail/list pages
 * - Agent ticket detail/list pages
 * - Guest ticket view pages (via token)
 * - Logged-in user "My Tickets" page
 */
export function useTicketWebSocket() {
  const queryClient = useQueryClient()
  const { agentUser, isAgentAuthenticated } = useAuthStore()
  const addToast = useToastNotificationStore(state => state.addToast)

  // Get siteId from environment or default
  const siteId = process.env.NEXT_PUBLIC_DEFAULT_SITE_ID || '1'

  // Build WebSocket URL with siteId query param
  // The siteId is captured during WebSocket upgrade and stored in connection metadata
  // This allows the server to broadcast ticket events to all connections from this site
  const wsUrl =
    typeof window !== 'undefined' ? `${getWebSocketUrl()}?siteId=${siteId}` : ''

  // Establish WebSocket connection with siteId in URL
  const { isConnected, registerAgent } = useWebSocket(wsUrl)

  const hasRegisteredRef = useRef(false)

  useEffect(() => {
    if (!isConnected || !isAgentAuthenticated || !agentUser?.id) {
      hasRegisteredRef.current = false
      return
    }

    if (hasRegisteredRef.current) {
      return
    }

    registerAgent(agentUser.id)
    hasRegisteredRef.current = true
  }, [agentUser?.id, isAgentAuthenticated, isConnected, registerAgent])

  useEffect(() => {
    const handleTicketUpdate = (event: CustomEvent) => {
      const { ticketId, type, data } = event.detail as {
        ticketId: number
        type: string
        data?: Record<string, any>
      }

      console.log(
        `🔄 Received ${type} event for ticket #${ticketId}, invalidating queries...`
      )

      // Invalidate all ticket-related queries for this specific ticket
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'detail', ticketId],
      })
      queryClient.invalidateQueries({
        queryKey: ['agent', 'tickets', 'detail', ticketId],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'replies', ticketId],
      })
      queryClient.invalidateQueries({
        queryKey: ['agent', 'tickets', 'replies', ticketId],
      })

      // Invalidate list queries to update ticket lists
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'list'],
      })
      queryClient.invalidateQueries({
        queryKey: ['agent', 'tickets', 'list'],
      })

      // Logged-in user ticket list
      queryClient.invalidateQueries({
        queryKey: ['my-tickets'],
      })

      // Public ticket queries
      queryClient.invalidateQueries({
        queryKey: ['public', 'tickets'],
      })

      // For guest token-based access, invalidate all ticket queries
      queryClient.invalidateQueries({
        predicate: query => {
          return query.queryKey[0] === 'ticket' && Array.isArray(query.queryKey)
        },
      })

      const createdBy = data?.createdBy
      const updatedBy = data?.updatedBy
      const actorId =
        typeof createdBy?.actorId === 'number'
          ? createdBy.actorId
          : typeof updatedBy?.actorId === 'number'
            ? updatedBy.actorId
            : null

      if (isAgentAuthenticated && agentUser) {
        const isSelfAction = actorId === agentUser.id
        const assignedToUserId =
          typeof data?.assignedToUserId === 'number'
            ? data.assignedToUserId
            : null
        const assignedToTeamId =
          typeof data?.assignedToTeamId === 'number'
            ? data.assignedToTeamId
            : null
        const teamIds =
          agentUser.teamIds && agentUser.teamIds.length > 0
            ? agentUser.teamIds
            : agentUser.primaryTeamId
              ? [agentUser.primaryTeamId]
              : []
        const matchesAssignment =
          assignedToUserId === agentUser.id ||
          (assignedToTeamId != null && teamIds.includes(assignedToTeamId))

        if (!isSelfAction && matchesAssignment) {
          const customerName =
            typeof data?.contactName === 'string'
              ? data.contactName
              : typeof data?.customerName === 'string'
                ? data.customerName
                : null
          const displayName = customerName?.trim() || 'Customer'
          const ticketLabel = data?.ticketNumber
            ? `Ticket ${data.ticketNumber}`
            : `Ticket #${ticketId}`

          if (type === 'ticket_created') {
            addToast({
              type: 'info',
              title: 'New ticket assigned',
              message: `${displayName} created ${ticketLabel}.`,
            })
          }

          if (type === 'ticket_reply_created') {
            addToast({
              type: 'info',
              title: 'New ticket reply',
              message: `${displayName} replied to ${ticketLabel}.`,
            })
          }
        }
      }
    }

    // Listen for custom ticket-update events from WebSocket service
    window.addEventListener(
      'ticket-update',
      handleTicketUpdate as EventListener
    )

    return () => {
      window.removeEventListener(
        'ticket-update',
        handleTicketUpdate as EventListener
      )
    }
  }, [addToast, agentUser, isAgentAuthenticated, queryClient])
}

// Legacy export for backwards compatibility
export const useTicketWebSocketSync = useTicketWebSocket
